import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./InviteUsers.css";

const ROLES = [
  { value: "loan_officer", label: "Loan Officer", icon: "🏦",
    description: "View their own loans and commissions. Submit exceptions.", color: "green" },
  { value: "manager",      label: "Manager",      icon: "👔",
    description: "View team loans and commissions. Approve exceptions.",     color: "blue"  },
  { value: "admin",        label: "Admin",        icon: "⚙️",
    description: "Full access. Upload data, manage all users.",              color: "red"   },
  { value: "executive",    label: "Executive",    icon: "📊",
    description: "Read-only view across the entire organization.",           color: "yellow"},
];

export default function InviteUsers() {
  const { user } = useAuth();
  const [step, setStep]           = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [email, setEmail]         = useState("");
  const [managerId, setManagerId] = useState("");
  const [managers, setManagers]   = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite]       = useState(null);
  const [copied, setCopied]       = useState(false);
  const [invites, setInvites]     = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);

  const availableRoles = user?.role === "manager"
    ? ROLES.filter(r => r.value === "loan_officer")
    : ROLES;

  const loadInvites = () => {
    api.get("/invites/").then(r => setInvites(r.data)).catch(() => {}).finally(() => setLoadingInvites(false));
  };

  useEffect(() => {
    api.get("/users/").then(r => setManagers(r.data.filter(u => u.role === "manager"))).catch(() => {});
    loadInvites();
  }, []);

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    if (user?.role === "manager") setManagerId(String(user.id));
    setStep(2);
  };

  const handleSend = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/invites/", {
        role: selectedRole.value,
        email: email || null,
        manager_id: managerId ? parseInt(managerId) : null,
      });
      setInvite(data);
      setStep(3);
      loadInvites();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not create invite");
    } finally { setSubmitting(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(invite.invite_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const reset = () => {
    setStep(1); setSelectedRole(null); setEmail("");
    setManagerId(""); setInvite(null); setCopied(false);
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite link?")) return;
    try {
      await api.delete(`/invites/${id}`);
      setInvites(invites.filter(i => i.id !== id));
    } catch { alert("Could not revoke invite"); }
  };

  const copyInlineLink = (url) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <div className="invite-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invite Users</h1>
          <p className="page-subtitle">Send a secure invite link to add someone to CommPro</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="invite-stepper">
        {["Select Role", "Customize", "Share Link"].map((label, i) => (
          <React.Fragment key={label}>
            <div className={`step ${step > i ? "done" : ""} ${step === i+1 ? "active" : ""}`}>
              <div className="step-circle">{step > i+1 ? "✓" : i+1}</div>
              <div className="step-label">{label}</div>
            </div>
            {i < 2 && <div className={`step-line ${step > i+1 ? "done" : ""}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="invite-card">
          <h2 className="invite-card-title">What type of user are you inviting?</h2>
          <p className="invite-card-subtitle">This determines what they can see and do in CommPro.</p>
          <div className="role-grid">
            {availableRoles.map(role => (
              <button key={role.value} className={`role-card role-${role.color}`} onClick={() => handleSelectRole(role)}>
                <div className="role-icon">{role.icon}</div>
                <div className="role-name">{role.label}</div>
                <div className="role-desc">{role.description}</div>
                <div className="role-select-hint">Select →</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && selectedRole && (
        <div className="invite-card">
          <button className="back-btn" onClick={() => setStep(1)}>← Back</button>
          <div className={`selected-role-badge role-${selectedRole.color}`}>
            {selectedRole.icon} Inviting a {selectedRole.label}
          </div>
          <h2 className="invite-card-title">Customize the invite</h2>
          <p className="invite-card-subtitle">
            Add their email to send them an invite automatically.
            Leave blank to just get a link you can share yourself.
          </p>
          <div className="invite-form">
            <div className="form-group">
              <label>Email Address <span className="optional-tag">optional — triggers auto-email</span></label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="they@company.com"
              />
              {email ? (
                <span className="field-hint field-hint-success">
                  ✉️ An invite email will be sent automatically when you click Generate.
                </span>
              ) : (
                <span className="field-hint">
                  Leave blank to skip the email — you'll get a link to share manually.
                </span>
              )}
            </div>

            {selectedRole.value === "loan_officer" && user?.role !== "manager" && (
              <div className="form-group">
                <label>Assign to Manager <span className="optional-tag">optional</span></label>
                <select value={managerId} onChange={e => setManagerId(e.target.value)}>
                  <option value="">— No manager assigned yet —</option>
                  {managers.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                </select>
              </div>
            )}

            <div className="invite-summary">
              <div className="summary-row"><span>Role</span>
                <span className={`summary-badge role-${selectedRole.color}`}>{selectedRole.label}</span>
              </div>
              {email && <div className="summary-row"><span>Email invite to</span><span>{email}</span></div>}
              <div className="summary-row"><span>Link expires</span><span>72 hours after creation</span></div>
            </div>

            <button className="btn-primary send-btn" onClick={handleSend} disabled={submitting}>
              {submitting ? "Creating invite..." : email ? "✉️ Send Invite Email" : "🔗 Generate Invite Link"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && invite && (
        <div className="invite-card success-card">
          <div className="success-icon">🎉</div>
          <h2 className="invite-card-title">
            {invite.email_sent ? "Invite email sent!" : "Invite link ready!"}
          </h2>

          {invite.email_sent ? (
            <p className="invite-card-subtitle">
              An invite email was sent to <strong>{invite.email}</strong>.
              The link expires in 72 hours. You can also copy it below to share another way.
            </p>
          ) : (
            <p className="invite-card-subtitle">
              Share this link with your new <strong>{selectedRole?.label}</strong>.
              It expires in 72 hours and can only be used once.
            </p>
          )}

          <div className="link-box">
            <span className="link-text">{invite.invite_url}</span>
            <button className={`copy-btn ${copied ? "copied" : ""}`} onClick={copyLink}>
              {copied ? "✓ Copied!" : "Copy"}
            </button>
          </div>

          {!invite.email_sent && (
            <div className="share-instructions">
              📋 <strong>How to share:</strong> Copy the link above and send it via email, Slack, or text.
              The recipient will be guided through a step-by-step account setup.
            </div>
          )}

          <button className="btn-secondary" onClick={reset} style={{marginTop:8}}>
            + Send Another Invite
          </button>
        </div>
      )}

      {/* Sent Invites */}
      <div className="sent-invites">
        <h2 className="section-title">Sent Invites</h2>
        {loadingInvites ? (
          <p className="loading-text">Loading...</p>
        ) : invites.length === 0 ? (
          <p className="loading-text">No invites sent yet.</p>
        ) : (
          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Email Sent</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map(inv => {
                  const expired = new Date() > new Date(inv.expires_at);
                  const status  = inv.is_used ? "used" : expired ? "expired" : "pending";
                  return (
                    <tr key={inv.id}>
                      <td><span className={`badge badge-${inv.role}`}>{inv.role.replace("_"," ")}</span></td>
                      <td className="mono" style={{color:"var(--text-secondary)"}}>{inv.email || "—"}</td>
                      <td>
                        {inv.email
                          ? <span className="badge badge-approved">✓ Sent</span>
                          : <span className="badge badge-expired">Link only</span>
                        }
                      </td>
                      <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                      <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge badge-${status === "pending" ? "approved" : status === "used" ? "paid" : "expired"}`}>
                          {status}
                        </span>
                      </td>
                      <td>
                        {status === "pending" && (
                          <div style={{display:"flex",gap:6}}>
                            <button className="copy-inline-btn" onClick={() => copyInlineLink(inv.invite_url)}>
                              Copy Link
                            </button>
                            <button className="revoke-btn" onClick={() => revokeInvite(inv.id)}>Revoke</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
